/**
 * Post-call recap: turn the existing AI analysis into 3-5 recap bullets and a
 * numbered action list. No new model call; this only reshapes what the
 * analyzer already stored (summary, key points, action items, next step).
 */

const MAX_BULLETS = 5;

function clean(s: string): string {
  return s.replace(/^[\s•\-*→\d.)]+/, '').trim();
}

/** Split a summary into sentence bullets, topped up with key points. */
export function recapBullets(summary: string | null | undefined, keyPoints?: string[] | null): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (raw: string) => {
    const t = clean(raw);
    const k = t.toLowerCase();
    if (t.length < 3 || seen.has(k) || out.length >= MAX_BULLETS) return;
    seen.add(k);
    out.push(t);
  };
  // The stored summary may carry the "Key Points:/Action Items:" tail the
  // analyzer appends; keep only the prose part.
  const prose = String(summary || '').split(/\n\s*\n(?:Key Points|Action Items):/i)[0];
  const lines = prose.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const bulletLike = lines.length > 1 && lines.every((l) => /^[•\-*→]|\d+[.)]/.test(l));
  const parts = bulletLike ? lines : prose.split(/(?<=[.!?])\s+(?=[A-Z0-9"'])/);
  parts.forEach(add);
  (keyPoints || []).forEach(add);
  return out;
}

/** The numbered action list: the reviewed next step first, then the analyzer's action items. */
export function actionItems(nextStep: string | null | undefined, items?: string[] | null): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of [nextStep || '', ...(items || [])]) {
    const t = clean(String(raw));
    const k = t.toLowerCase();
    if (!t || seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}
